import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CircularProgress } from '@mui/material';

import { EmailReportSeriesBucketOutDto } from '@app/swagger-types';

// colour-blind-validated series palette from the real UI kit (admin-email-mockup_3.html §9 /
// --s1..--s4), not chosen freely - keep in sync if the mockup's palette ever changes.
const CATEGORY_COLOR: Record<string, string> = {
  ACCOUNT: '#2C9424',
  QR_NOTIFICATION: '#2A78D6',
  TRIAL_LIFECYCLE: '#EB6834',
  TIPS: '#4A3AA7',
  OTHER: '#9CA3AF',
};

// admin-emailbeheer blok B (opdracht §4, "Grafiek 1"): stacked per category so a shift from
// QR_NOTIFICATION down to TRIAL_LIFECYCLE/TIPS is visible, not just total volume.
export const EmailReportVolumeChart: React.FC<{
  buckets: EmailReportSeriesBucketOutDto[];
  loading?: boolean;
}> = ({ buckets, loading }) => {
  const { data, categories } = useMemo(() => {
    const categorySet = new Set<string>();
    const rows = buckets.map((bucket) => {
      Object.keys(bucket.sentByCategory).forEach((c) => categorySet.add(c));
      return { label: bucket.label, partial: bucket.partial, ...bucket.sentByCategory };
    });
    return { data: rows, categories: Array.from(categorySet) };
  }, [buckets]);

  if (loading) {
    return (
      <div className="flex h-[280px] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 16 }}>
        <CartesianGrid stroke="#E5E7EB" vertical={false} />
        <XAxis dataKey="label" tickLine={false} axisLine={false} />
        <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
        <Tooltip cursor={false} wrapperClassName="rounded" />
        <Legend />
        {categories.map((category) => (
          <Bar
            key={category}
            dataKey={category}
            stackId="sent"
            fill={CATEGORY_COLOR[category] ?? '#9CA3AF'}
            radius={category === categories[categories.length - 1] ? [4, 4, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
